import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';

// ====== Import schemas from generated ======
const {
  ProductCategoryFirestoreResponse,
  CreateCategoryDto,
  UpdateCategoryDto,
  DeleteResponseDto,
} = schemas;

// ====== Comparison Schema (not in generated) ======
export const CategoryComparisonResultSchema = z.object({
  exists: z.boolean(),
  supabaseData: ProductCategoryFirestoreResponse.nullable(),
}).passthrough();

// ====== Re-export schemas for external use ======
export const ProductCategoryResponseSchema = ProductCategoryFirestoreResponse;
export const CreateProductCategorySchema = CreateCategoryDto;
export const UpdateProductCategorySchema = UpdateCategoryDto;

// ====== Type Exports ======
export type ProductCategoryResponse = z.infer<typeof ProductCategoryFirestoreResponse>;
export type CreateProductCategory = z.infer<typeof CreateCategoryDto>;
export type UpdateProductCategory = z.infer<typeof UpdateCategoryDto>;
export type CategoryComparisonResult = z.infer<typeof CategoryComparisonResultSchema>;

/**
 * Create product categories API factory
 */
export function createProductCategoriesApi(client: TypedApiClient) {
  return {
    /**
     * Get all product categories
     */
    getProductCategories: (params?: {
      parentId?: string;
    }): Promise<ProductCategoryResponse[]> => {
      return client.get('/product-categories', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(ProductCategoryFirestoreResponse),
      });
    },

    /**
     * Create a new product category
     */
    createProductCategory: (data: CreateProductCategory): Promise<ProductCategoryResponse> => {
      return client.post('/product-categories', {
        data,
        requestSchema: CreateCategoryDto,
        responseSchema: ProductCategoryFirestoreResponse,
      });
    },

    /**
     * Update an existing product category
     */
    updateProductCategory: (id: string, data: UpdateProductCategory): Promise<ProductCategoryResponse> => {
      return client.put(apiPath`/product-categories/${id}`, {
        data,
        requestSchema: UpdateCategoryDto,
        responseSchema: ProductCategoryFirestoreResponse,
      });
    },

    /**
     * Delete a product category.
     *
     * ATL-1009 — `moveToCategoryId` is the Firestore id of the category this one's products move
     * to, mirroring the same reassignment the app writes into Firestore in the delete batch. Pass
     * `''` for "no category". Omitting it entirely leaves `products.category_id` pointing at the
     * category that is about to be soft-deleted, which is what produced the stale-category reads
     * this parameter exists to stop.
     */
    deleteProductCategory: (
      id: string,
      moveToCategoryId?: string,
    ): Promise<z.infer<typeof DeleteResponseDto>> => {
      return client.delete(apiPath`/product-categories/${id}`, {
        params: moveToCategoryId === undefined ? undefined : { moveToCategoryId },
        responseSchema: DeleteResponseDto,
      });
    },

    /**
     * Compare category with Firestore data
     */
    compareWithFirestore: (firestoreId: string): Promise<CategoryComparisonResult> => {
      return client.get(apiPath`/product-categories/compare/${firestoreId}`, {
        responseSchema: CategoryComparisonResultSchema,
      });
    },
  };
}
